import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, rem, Text, TextInput} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {ReactNode, useState} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import MantineBaseEditableField, {hasFieldError} from 'ui-library/MantineBaseEditableField';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

const DEFAULT_EMPTY_VIEW_MODE_TEXT = 'No value';

const TEXT_INPUT_WRAPPER_ORDER: Array<'input'> = ['input'];

/** rem, not px: tracks Mantine xs input height (root font-size is 62.5%). */
const SINGLE_LINE_CONTENT_HEIGHT = rem(28);

/** Match Mantine's rem-based input padding (`size/3` + border). Don't convert to px. */
const XS_INPUT_TEXT_INSET = rem(11);

export interface MantineEditableTextFieldProps {
  allowEmpty?: boolean;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  /** Automatically sets disabled behavior when disabled tooltip is provided. */
  disabledTooltip?: string | null;
  /** Shown in view mode when `value` is empty. */
  emptyViewModeText?: string;
  /**
   * Shown in edit mode only; disables Save/Enter. Drive this from `value` / `onDraftChange`,
   * not from `onCommit` failures — edit closes on commit.
   */
  error?: string | null;
  /** Seeds edit mode; later changes are ignored. */
  initialEditing?: boolean;
  label?: ReactNode;
  /** Id of an existing caption used as the accessible name. */
  labelledBy?: string;
  maxLength?: number;
  onCommit: (value: string) => void;
  /**
   * Keystrokes, and cancel when the draft differs from `value`.
   * Not called on start-edit, no-op cancel, or commit.
   */
  onDraftChange?: (value: string) => void;
  onEditingChange?: (isEditing: boolean) => void;
  placeholder?: string;
  value: string;
}

export default function MantineEditableTextField({
  allowEmpty = false,
  dataTestAttrs,
  disabled = false,
  disabledTooltip,
  emptyViewModeText = DEFAULT_EMPTY_VIEW_MODE_TEXT,
  error,
  initialEditing = false,
  label,
  labelledBy,
  maxLength,
  onCommit,
  onDraftChange,
  onEditingChange,
  placeholder,
  value,
}: MantineEditableTextFieldProps): JSX.Element {
  const inputId = useId();
  const {classes} = useStyles();
  const [draftValue, setDraftValue] = useState(value);
  const [isEditing, setIsEditing] = useState(initialEditing);
  const hasError = hasFieldError(error);
  const isInteractionDisabled = disabled || disabledTooltip != null;
  const canCommit = (allowEmpty || draftValue !== '') && !hasError;
  const isEmpty = value === '';

  function setEditing(nextIsEditing: boolean): void {
    setDraftValue(value);
    setIsEditing(nextIsEditing);
    onEditingChange?.(nextIsEditing);
  }

  function handleCancel(): void {
    const hadDraftChange = draftValue !== value;
    setEditing(false);
    if (hadDraftChange) {
      onDraftChange?.(value);
    }
  }

  function handleStartEdit(): void {
    setEditing(true);
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      if (canCommit) {
        handleCommit();
      }
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      handleCancel();
    }
  }

  function handleCommit(): void {
    onCommit(draftValue);
    setEditing(false);
  }

  function handleChange(e: InputChangeEvent): void {
    const nextValue = e.target.value;
    setDraftValue(nextValue);
    onDraftChange?.(nextValue);
  }

  return (
    <MantineBaseEditableField
      classNames={{
        error: classes.inset,
        label: classes.inset,
        shell: classes.shell,
      }}
      dataTestAttrs={dataTestAttrs}
      disabled={isInteractionDisabled}
      disabledTooltip={disabledTooltip}
      editor={
        <TextInput
          aria-labelledby={labelledBy}
          autoFocus={true}
          classNames={{
            input: classes.input,
            wrapper: classes.inputWrapper,
          }}
          disabled={isInteractionDisabled}
          error={hasError ? error : undefined}
          id={inputId}
          // Otherwise Mantine also renders label/error, duplicating the base.
          inputWrapperOrder={TEXT_INPUT_WRAPPER_ORDER}
          maxLength={maxLength}
          mb={0}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="xs"
          value={draftValue}
          w="100%"
          {...getTestProps({component: 'MantineEditableTextField', element: 'input'})}
        />
      }
      editorId={inputId}
      error={error}
      isEditing={isEditing}
      isSaveEnabled={canCommit}
      label={label}
      labelledBy={labelledBy}
      onCancel={handleCancel}
      onSave={handleCommit}
      onStartEdit={handleStartEdit}
      viewer={
        <Text
          className={classes.inset}
          color={isEmpty ? 'dimmed' : undefined}
          miw={0}
          size="xs"
          truncate={true}
          w="100%"
          {...getTestProps({component: 'MantineEditableTextField', element: 'viewer'})}
        >
          {isEmpty ? emptyViewModeText : value}
        </Text>
      }
    />
  );
}

const useStyles = createStyles((theme) => ({
  // Pad the shell so view-mode hover matches text input height.
  shell: {
    minHeight: `calc(${SINGLE_LINE_CONTENT_HEIGHT} + 2 * ${theme.spacing.xs})`,
  },
  input: {
    height: '100%',
    minHeight: 'unset',
    // Mantine's invalid style also reddens the text.
    '&[data-invalid]': {
      borderColor: theme.colors.red[6],
      color: theme.colors.gray[9],
    },
  },
  inputWrapper: {
    height: '100%',
  },
  // Label, error, and viewer: match Mantine xs TextInput horizontal padding.
  inset: {
    paddingLeft: XS_INPUT_TEXT_INSET,
  },
}));
